import {
  BarChart3,
  CalendarDays,
  FileText,
  Inbox,
  Layers,
  Megaphone,
  Settings,
  UserCircle,
  Users,
  Wallet
} from 'lucide-react';

// Item keys: url gate, to target, any_of, force, action, ext, label; sections carry a lucide icon.

const SESSION_PAGES = [
  '/sessions',
  '/pastsessions',
  '/pastreplacements',
  '/replacementsessions',
  '/cancelledsessions',
  '/friends_sessions',
  '/friends_sessions/past',
  '/request_session_dashboard'
];

export const MENU_SECTIONS = [
  {
    id: 'people',
    icon: Users,
    title: 'People',
    items: [
      { name: 'Students', url: '/users' },
      { name: 'Student Profiles', url: '/student_profiles' },
      { name: 'Teachers List', url: '/teacherslist' },
      { name: 'Hidden Teachers', url: '/teacherslist', to: '/teacherslist/hidden' },
      { name: 'Teacher Match', url: '/teacher-match' },
      { name: 'Trial Points', url: '/trial_points' }
    ]
  },
  {
    id: 'sessions',
    icon: CalendarDays,
    title: 'Sessions',
    items: [
      { name: 'Request a Session', url: '/request_session_dashboard' },
      { name: 'Future Sessions', url: '/sessions' },
      { name: 'Past Sessions', url: '/pastsessions' },
      { name: 'Pending Replacements', url: '/replacementsessions' },
      { name: 'Past Replacements', url: '/pastreplacements' },
      { name: 'Cancelled Sessions', url: '/cancelledsessions' },
      { name: 'Friends Sessions', url: '/friends_sessions' },
      { name: 'Past Friends Sessions', url: '/friends_sessions/past' },
      { name: 'Future Consults', url: '/consults/future' },
      { name: 'Past Consults', url: '/consults/past' },
      { name: 'GPT Session', url: '/gpt_session_notes' },
      { name: 'QA Scores', url: '/qa_scores' },
      { name: 'Recommendations Playground V2', url: '/recommendations_playground_v2' },
      {
        name: 'Upcoming 1st 1on1 Sessions (Tableau)',
        any_of: SESSION_PAGES,
        ext: 'https://prod-useast-a.online.tableau.com/#/site/myyogateacherinc/views/BackPainSessions/Upcoming1st1on1Sessions?:iid=1'
      },
      {
        name: 'Upcoming 1st 1on1 Sessions (Redash)',
        any_of: SESSION_PAGES,
        ext: 'https://redash.azure-services.myyogateacher.com/queries/594?p_date_filter=today'
      }
    ]
  },
  {
    id: 'classes',
    icon: Layers,
    title: 'Classes & events',
    items: [
      { name: 'Repeat Classes', url: '/groupsessions/repeat' },
      { name: 'Future Classes', url: '/groupsessions' },
      { name: 'Past Classes', url: '/pastgroupsessions' },
      { name: 'Class Types', url: '/group_class_types' },
      { name: 'Class Pages', url: '/group_class_pages' },
      { name: 'Community Class', url: '/community_class' },
      { name: 'Events', url: '/events' },
      { name: 'Workshops', url: '/workshops' },
      { name: 'YWF Groups', url: '/ywf_repeat_groups' },
      { label: 'Backpain' },
      { name: 'Future Group Classes', url: '/backpain/groupsessions' },
      { name: 'Past Group Classes', url: '/backpain/pastgroupsessions' },
      { name: 'Future Backpain Sessions', url: '/sessions/backpain' },
      { name: 'Past Backpain Sessions', url: '/pastsessions/backpain' },
      { name: 'Backpain Class Page', url: '/backpain/group_class_pages' }
    ]
  },
  {
    // All tabs sit behind the one /teacher-payments page access.
    id: 'teacher_payments',
    icon: Wallet,
    title: 'Teacher payments',
    items: [
      { name: 'Overview', url: '/teacher-payments', to: '/teacher-payments/overview' },
      {
        name: 'Needs attention',
        url: '/teacher-payments',
        to: '/teacher-payments/needs-attention'
      },
      { name: 'Penalties', url: '/teacher-payments', to: '/teacher-payments/penalties' },
      {
        name: 'Additional payments',
        url: '/teacher-payments',
        to: '/teacher-payments/additional-payments'
      },
      { name: 'Reports', url: '/teacher-payments', to: '/teacher-payments/reports' },
      { name: 'Rules', url: '/teacher-payments', to: '/teacher-payments/rules-engine' }
    ]
  },
  {
    id: 'reports',
    icon: BarChart3,
    title: 'Reports',
    items: [
      { label: 'Concierge' },
      {
        name: 'Concierge MyYogaTeacher',
        url: '/concierge/lifestyle',
        to: '/concierge/lifestyle/v3'
      },
      { name: 'Buckets', url: '/concierge/lifestyle/post-trial-buckets' },
      { name: 'Concierge V2 (New)', url: '/concierge/lifestyle/v2' },
      { name: 'Concierge LifetreeHealth', url: '/concierge/backpain' },
      { label: 'Students' },
      { name: 'New Students', url: '/newusers' },
      { name: 'Incomplete Onboarding', url: '/incomplete_onboarding' },
      { name: 'Students and Sessions', url: '/studentsandsessions' },
      { name: 'Student Session Stats', url: '/studentsessionstats' },
      { name: 'Ten Days Inactive Members', url: '/ten_days_inactive_users' },
      { name: 'Banned Students', url: '/banned_students' },
      { name: 'Referrals', url: '/referrals' },
      { label: 'Payments' },
      { name: 'Transactions', url: '/allstudenttransactions' },
      { name: 'Payment Failures', url: '/payment_failures' },
      { name: 'Failed Recurring Transactions', url: '/failedrecurringtransactions' },
      { name: 'Chargebacks', url: '/chargebacks' },
      { name: 'Membership Cancellations', url: '/membership_cancellations' },
      { name: 'Extra Participant Charges', url: '/extra_participant_charges' },
      { name: 'Nutrition Program Payments', url: '/nutrition_program_payments' },
      { label: 'Teachers' },
      { name: 'Teacher Applications', url: '/teacher-application' },
      { name: 'Teachers Availability', url: '/availability' },
      { name: 'Consult Teachers Availability', url: '/availability/consult' },
      { name: 'Teacher Session Stats', url: '/teachersessionstats' },
      { name: 'Replacement Report', url: '/replacement' },
      { name: 'Manual Cropping', url: '/sessions-for-manual-cropping' },
      { label: 'Messages' },
      { name: 'Care Messages', url: '/caremessages' },
      { name: 'Blocked Messages', url: '/blockedmessages' },
      { name: 'Email Delivery Report', url: '/emaildeliveryreport' },
      { name: 'SMS Report', url: '/sms_report', to: '/sms_report/v3' },
      { name: 'Flagged SMS Report', url: '/flagged_sms' },
      { label: 'Business' },
      { name: 'Key Metrics', url: '/keymetrics' },
      { name: 'Weekly Report', url: '/weekly_report' },
      { name: 'Time Based Cohort', url: '/cohorttimebased' },
      { name: 'Group Sessions Report', url: '/groupsessionsreport' }
    ]
  },
  {
    id: 'marketing',
    icon: Megaphone,
    title: 'Marketing',
    items: [
      { name: 'Coupon Offers', url: '/coupon_offers' },
      { name: 'Pricing Links', url: '/pricing_links' },
      { name: 'Magic Link Campaigns', url: '/magic-links' },
      { name: 'Short Url Links', url: '/short-urls' },
      { name: 'Affiliates', url: '/affiliates' },
      { name: 'Announcements', url: '/announcements' },
      { name: 'Split Testing', url: '/split_testing' },
      { name: 'Split Testing V2', url: '/split_test_v2' },
      { name: 'Create Email', url: '/createemail' },
      { name: 'Email Templates', url: '/email-templates-list' },
      { name: 'Feedback Questions', url: '/feedback-forms' },
      { name: 'MoEngage User Export', url: '/moengage-export' }
    ]
  },
  {
    id: 'content',
    icon: FileText,
    title: 'Content',
    items: [
      { name: 'Content Tags', url: '/content/tags' },
      { name: 'Press Pages', url: '/press_pages' },
      { name: 'Upload Workshop Image', url: '/upload_images/workshop' },
      { name: 'Upload Blog Image', url: '/upload_images/blog' },
      { name: 'Health Marker', url: '/health_marker', force: true },
      { name: 'Invalidate Cache', url: '/invalidate-cache' }
    ]
  },
  {
    id: 'work',
    icon: Inbox,
    title: 'Work queue',
    items: [
      { name: 'Twilio Agent', url: '/twilio-agent' },
      { name: 'Support Inbox', url: '/support' },
      { name: 'Student Tickets / Chat', url: '/chat', action: 'open_student_chat' },
      { name: 'Teacher Tickets / Chat', url: '/chat', action: 'open_teacher_chat' },
      { name: 'Sales Task List', url: '/sales-task-list' },
      { name: 'Early Retention Checklist', url: '/early-retention-checklist' },
      { name: 'Reminders', url: '/reminders' }
    ]
  },
  {
    id: 'admin',
    icon: Settings,
    title: 'Admin & tools',
    items: [
      { name: 'CMS Users', url: '/cms-users' },
      { name: 'Upload Teacher Payments', url: '/upload_teacher_payments' },
      { name: 'Download Data', url: '/download' },
      { name: 'Profanity List', url: '/profanity_list' },
      { name: 'Chat Groups', url: '/chat-groups' },
      { name: 'Zoom Meeting Users', url: '/zoom-meeting-users' },
      { name: 'Vidya', url: '/vidya' }
    ]
  },
  {
    id: 'profile',
    icon: UserCircle,
    title: 'Profile',
    items: [
      { name: 'My Profile', url: '/profile', force: true },
      { name: 'My Preferences', url: '/cms-users-config', force: true },
      { name: 'Log out', action: 'log_out', force: true }
    ]
  }
];

// Where an item navigates to.
export const item_target = (item) => item.to || item.url;

// Stable React key / identity for an item.
export const item_key = (item) => item.action || item.ext || item_target(item);

export function can_see_item(item, access_pages = []) {
  if (item.force) return true;
  if (item.any_of) return item.any_of.some((page) => access_pages.indexOf(page) !== -1);
  return access_pages.indexOf(item.url) !== -1;
}

// Sections the user can open: hidden items, empty sub-headings and empty sections removed.
export function visible_sections(sections, access_pages = []) {
  return sections
    .map((section) => {
      const items = [];
      let pending_label = null;
      section.items.forEach((item) => {
        if (item.label) {
          pending_label = item;
          return;
        }
        if (!can_see_item(item, access_pages)) return;
        if (pending_label) {
          items.push(pending_label);
          pending_label = null;
        }
        items.push(item);
      });
      return { ...section, items };
    })
    .filter((section) => section.items.some((item) => !item.label));
}

// Sections whose item names contain the query (case-insensitive); sub-headings are dropped while searching.
export function filter_sections(sections, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return sections;
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.label && item.name.toLowerCase().includes(q))
    }))
    .filter((section) => section.items.length > 0);
}

// The item for the current path (longest matching target wins) and its section id, or nulls.
export function active_item(sections, pathname) {
  let best = { section_id: null, key: null, length: -1 };
  sections.forEach((section) => {
    section.items.forEach((item) => {
      const target = !item.label && !item.action && !item.ext ? item_target(item) : null;
      if (!target) return;
      const matches = pathname === target || String(pathname || '').startsWith(`${target}/`);
      if (matches && target.length > best.length) {
        best = { section_id: section.id, key: item_key(item), length: target.length };
      }
    });
  });
  return { section_id: best.section_id, key: best.key };
}
